<template lang="pug">
	.audio-tracker(ref="audioTracker", @mousemove="onMouseMove", @mousedown="onMouseDown", @mouseup="onMouseUp")
		.resize-handler(ref="resizeHandler")
		.flex-col(v-show="!hidden")
			.actions.flex-row.justify
				.left-actions.flex-row.space.align-center.wrap.full-height.margin-bottom
					template(v-if="isInitialized && panelVisible")
						.flex-row.space.align-center.wrap.margin-bottom(v-if="!isPublishMode", :class="{ 'audio-tracker-readonly': readonly }")
							slot(name="actions-left-start-common", :isLoading="isLoading")
						.flex-row.space.align-center.wrap.margin-bottom(v-if="!isPublishMode", :class="{ 'audio-tracker-readonly': focusedAggregatedTrack ? focusedAggregatedTrack.__readonly : readonly }")
							slot(name="actions-left-start-track", :isLoading="isLoading")
						.flex-row.space.align-center.wrap.margin-bottom(v-if="!isPublishMode", :class="{ 'audio-tracker-readonly': readonly }")
							slot(name="actions-left-start", :isLoading="isLoading")
						AudioTrackerButtonGroup
							AudioTrackerButton(:title="$tCaption('Backward')", icon="fas fa-backward", @click="backward()", :disabled="currentTime === 0")
							AudioTrackerButton(:title="$tCaption('Play', '@actions')", :icon="isPlayingInitiating ? 'fas fa-spinner' : (isPlaying ? 'fas fa-pause' : 'fas fa-play')", :spin="isPlayingInitiating", @click="togglePlay", :disabled="!visibleTracks.length")
							AudioTrackerButton(:title="$tCaption('Stop', '@actions')", icon="fas fa-stop", @click="stop", :disabled="!visibleTracks.length && !isPlaying")
						.flex-row.space.align-center.wrap.margin-bottom(v-if="!isPublishMode")
							slot(name="actions-left", :isLoading="isLoading")
						template(v-if="panelVisible && !isPublishMode")
							AudioTrackerMasterTrackHeadPanel(v-if="!hidden", ref="masterTrack", :tracker="self", :class="{ 'audio-tracker-readonly': readonly }", :filters="masterTrackEffects && masterTrackEffects.filters", :audioContext="audioContext", :config="masterTrackConfig", @changed="onMasterTrackConfigChanged")
								template(v-slot:post-effects-left="data")
									slot(name="master-track-post-effects-left", v-bind="data")
							.panel-indicator(:title="$tCaption('Total creative duration')")
								.font-tiny.upper.grey.flex-row.flex-1.center.align-center {{$tCaption('Duration')}}
								.flex-row.flex-1.center.align-center.value.font-small {{$Data.secondsToDisplayValue(totalVisibleDuration)}}
							.panel-indicator.clip-indicator(:title="$tCaption('Selected clip duration')", :class="{ 'disabled-controls': !selectedTrack }")
								.font-tiny.upper.grey.flex-row.flex-1.center.align-center {{$tCaption('Clip')}}
								.flex-row.flex-1.center.align-center.value.font-small {{selectedTrack ? $Data.secondsToDisplayValue(selectedTrack.trimmedDuration) : '-'}}
						slot(name="actions-left-end", :isLoading="isLoading")
						AudioTrackerButton(:title="$tCaption('Shortcuts')", icon="fas fa-keyboard", @click="$refs.shortcuts.show()")
						AudioTrackerButton.highlight-scaled(v-if="copyPaste.buffer", :title="$tCaption('Paste audio buffer')", icon="fas fa-paste", @click="pasteBuffer", :spin="!!copyPaste.isLoading")
						AudioTrackerButtonGroup
							AudioTrackerButton(:title="$tCaption('Range selection')", icon="fas fa-vector-square", @click="toggleRangeSelector", :active="rangeSelection.visible", :class="{ 'highlight-scaled': rangeSelection.visible }")
							AudioTrackerButton(:title="$tCaption('Cycle loop')", icon="fas fa-repeat", @click="toggleRepeat", :active="playbackFunctions.repeat", :class="{ 'highlight-scaled': playbackFunctions.repeat }")
				.right-actions.flex-row.align-center.wrap.full-height.margin-bottom.justify-end.space(v-if="!isPublishMode && isInitialized")
					slot(v-if="!isPublishMode", name="actions-right", :isLoading="isLoading")
					AudioTrackerButtonGroup
						AudioTrackerButton(:title="$tCaption('Zoom in')", @click="v => zoom(2)", icon="fas fa-plus")
						AudioTrackerButton(:title="$tCaption('Zoom out')", @click="v => zoom(0.5)" icon="fas fa-minus")
			AudioTrackerVideoPlayer(v-if="isVideoSupported", ref="videoPlayer", :isPlaying="isPlaying", :tracker="self", :currentTime="currentTime", :track="activeVideoTrack" :videoData="activeVideoTrack && activeVideoTrack.__base64",
				:nextTrack="activeVideoTrackNext", :nextVideoData="activeVideoTrackNext && activeVideoTrackNext.__base64")
			.flex-row(v-if="!isPublishMode")
				DxScrollView.track-infos(v-if="aggregatedTracks && aggregatedTracks.length", :useNative="false", ref="infoPanelsScroll", :disabled="true", :width="200",
					:height="getNavigatorHeight(bottomSpace)", showScrollbar="never")
					Container.tracks.flex-col.tracks-panels(orientation="vertical", @drop="onTrackInfoPanelDrop", :drag-handle-selector="trackDragHandle || '.name-field'")
						Draggable(v-for="item in tracksListProp", :key="item.key")
							AudioTrackInfoPanel(:key="item.key",
								:ref="`trackInfo_${item.key}`",
								:track="getCurrentPlayingEffects(item.key).track || item.track",
								:aggregatedTrack="item.aggregatedTrack",
								:source="(opts) => initTrackSource(opts && opts.track || item.track, 0)",
								:audioContext="audioContext",
								:effects="getCurrentPlayingEffects(item.key).filters",
								:isSolo="!!(soloTrackId !== null && soloTrackId == item.key)",
								:class="{ subtrack: item.track.__isSubTrack, 'audio-tracker-readonly': item && item.aggregatedTrack ? item.aggregatedTrack.__readonly : readonly }",
								:tracker="self",
								@solo="v => { setSoloTrack(item); }",
								@mute="v => { item.aggregatedTrack.muted = !item.aggregatedTrack.muted; if (soloTrackId === item.key) soloTrackId = null; refreshPlay(); }",
								@delete="v => $emit('delete-track', item.track)",
								@delete-tracks="v => $emit('delete-tracks', v)",
								@delete-aggregated-track="v => $emit('delete-aggregated-track', v)",
								@effects-changed="onTrackEffectsChanged",
								@volume-changed="onTrackVolumeChanged",
								@size-changed="() => drawTrack(item.key)",
								@preview-plugins="previewPlugins"
							)
								template(v-slot:actions-left="data")
									slot(name="info-panel-actions-left", v-bind="data")
								template(v-slot:actions-right="data")
									slot(name="info-panel-actions-right", v-bind="data")
								template(v-if="$scopedSlots['track-name']", v-slot:track-name="data")
									slot(name="track-name", v-bind="data")
								template(v-if="$scopedSlots['track-compact-right-actions']", v-slot:track-compact-right-actions="data")
									slot(name="track-compact-right-actions", v-bind="data")
								template(v-if="$scopedSlots['post-effects-left']", v-slot:post-effects-left="data")
									slot(name="post-effects-left", v-bind="data")
								template(v-if="$scopedSlots['post-effects-right']", v-slot:post-effects-right="data")
									slot(name="post-effects-right", v-bind="data")
								AudioTrackSubTracksFilterPanel(v-if="!item.track.__isSubTrack && selectedMainTrack && selectedMainTrack.__aggregatedTrack === item.aggregatedTrack", :track="selectedMainTrack", :aggregatedTrack="item.aggregatedTrack")
				.flex-col.flex-1.navigator-panel(@mousedown="navigatorPanelMouseDown = true", @mouseup="navigatorPanelMouseDown = false")
					DxScrollView.ruler-scroll(ref="rulerScroll", :useNative="false", :disabled="true", :width="getNavigatorWidth(10)", :height="15", showScrollbar="never", direction="both")
						.tracks-navigator.flex-col.flex-1
							.ruler-holder(ref="rulerHolder", @click="onTrackClick")
								AudioTrackerRuler(ref="ruler", :totalTime="totalDuration + 1", :pixelsPerSecond="display.pixelsPerSecond", :width="getNavigatorWidth")
									.current-time-value(v-if="!(toolsTrack && refsTools() && refsTools().selectionPointVisible)", ref="rulerCurrentTime") {{$Data.secondsToDisplayValue(lastTime)}}
									.tracker--ruler-range-selection(v-if="rangeSelection.visible", ref="rulerRangeSelection", @mousedown.stop="onTrackerRulerRangeSelectionClick")
					DxScrollView.navigator-scroll(ref="navigatorScroll", :width="getNavigatorWidth(10)", :height="getNavigatorHeight(bottomSpace)", showScrollbar="always", direction="both", @scroll="onNavigatorScroll")
						.tracks-navigator.flex-col.flex-1(ref="navigator")
							.tracks
								.track(v-for="item in tracksListProp", :key="item.key", :ref="`track_${item.key}`", @click="onTrackClick"
									v-if="trackListPropHeights[item.key]",
									:style="{ height: `${trackListPropHeights[item.key] || 0}px`, width: `${getTrackWidth()}px` }"
									@mousedown="e => onTrackMouseDown(e, item.track, item)",
									@mouseup="e => onTrackMouseUp(e, item.track, item)",
									@mouseover="() => { if (item.isAggregatedTrack) { setMouseOverAggregatedTrack(item.key); } }",
									:class="[item.key === getMouseOverAggregatedTrack() ? 'mouseover' : '', item.track && item.track.__isSubTrack ? 'sub-track' : '']"
								)
									.track-line.flex-row.align-center(:ref="`trackLine_${item.key}`", @click="setFocusedAggregatedTrack(item.aggregatedTrack)", :class="[ focusedAggregatedTrack && focusedAggregatedTrack.trackNumber === item.aggregatedTrack.trackNumber ? 'focused' : '' ]")
										template(v-for="track in item.tracks")
											AudioTrackerCanvas(:key="track.id", :aggregatedTrack="item.aggregatedTrack", :subTrackNumber="item.number", :totalSubTracks="track && track.__parentTrack && track.__parentTrack.__totalSubInsertions || 0"
												:tracker="self", :ref="`trackCanvas_${track.id}`", @dblclick="track => $emit('track-dblclick', track)", :track="track",
												:disabled="isMultitrackActive || !track.__isSrcLoaded", @click="onTrackClick", @mousemove="onMouseMove",
												:readonly="item && item.aggregatedTrack ? item.aggregatedTrack.__readonly : readonly", :activeToolId="activeToolId", :navigatorWidth="getNavigatorWidth()", :navigatorHeight="getNavigatorHeight()",
												@update-tracks="() => updateTracksList(true)")
												template(v-slot:actions-left-static="data")
													slot(name="canvas-actions-left-static", v-bind="data")
												template(v-slot:actions-left="data")
													slot(name="canvas-actions-left", v-bind="data")
												template(v-slot:actions-left-inactive="data")
													slot(name="canvas-actions-left-inactive", v-bind="data")
												template(v-slot:actions-right="data")
													slot(name="canvas-actions-right", v-bind="data")
												template(v-slot:actions-right-static="data")
													slot(name="canvas-actions-right-static", v-bind="data")
												template(v-slot:canvas="data")
													slot(name="canvas-area", v-bind="data")
												template(v-slot:actions-right-inactive="data")
													slot(name="canvas-actions-right-inactive", v-bind="data")
												template(v-if="$scopedSlots['canvas-track-name']", v-slot:track-name="data")
													slot(name="canvas-track-name", v-bind="data")
												template(slot="tools")
													AudioTrackerTools.tools-panel(v-if="toolsTrack && toolsTrack.id === track.id", ref="tools", :track="toolsTrack", :tracker="self",
														@tool-changed="onToolChanged", @copy-buffer="onCopyBuffer",
														@finished="() => { toolsTrack = null; }", @duplicate-track="(track, data) => $emit('duplicate-track-tool', track, data)")
								.tracker-overlay
									AudioTrackerTrackOverlay(:tracker="self", :tracks="tracks", :showLinksAlways="showLinksAlways", :aggregatedTracks="aggregatedTracks")
							.current-time(v-if="!(toolsTrack && refsTools() && refsTools().selectionPointVisible)", ref="currentTime")
							.selection(v-if="toolsTrack && refsTools() && refsTools().selectionVisible", :style="{ left: `${refsTools().selection.start * display.pixelsPerSecond}px`, width: `${refsTools().selection.duration * display.pixelsPerSecond}px` }")
							.selection-point.active(v-else-if="toolsTrack && refsTools() && refsTools().selectionPointVisible && refsTools().selection.start", :style="{ left: `${refsTools().selection.start * display.pixelsPerSecond}px` }")
							.selection-point(v-if="toolsTrack && refsTools() && refsTools().selectionPointVisible && $mouseMoveTimePosition", :style="{ left: `${$mouseMoveTimePosition * display.pixelsPerSecond}px` }")
							AudioTrackerMultitrackSelection(v-if="!readonly", ref="multitrackSelector", :tracker="self", @shift="v => isMultitrackActive = v", v-model="selectedTrackIds")
							AudioTrackerRegionSelectionUI(ref="regionSelectionUI", :tracker="self")
		AudioTrackerMasterTrackHeadPanel(v-if="hidden", :hidden="hidden", ref="masterTrack", :tracker="self", :class="{ 'audio-tracker-readonly': readonly }", :filters="masterTrackEffects && masterTrackEffects.filters", :audioContext="audioContext", :config="masterTrackConfig", @changed="onMasterTrackConfigChanged")
			template(v-slot:post-effects-left="data")
				slot(name="master-track-post-effects-left", v-bind="data")
		AudioTrackerRecorderPopup(v-if="!isPublishMode", ref="recorder", :tracker="self", :track="editingTrack", :audioContext="audioContext", :showUI="false", @popup="v => setTracksActive(!v)", @save="onAudioRecorded")
		WAPIPluginPopup(v-if="!isPublishMode", ref="wapiPlugin", :plugins="wapiPlugin.aggregatedTrack && wapiPlugin.aggregatedTrack.wapiPlugins", :audioContext="audioContext",
			:source="(opts) => initTrackSource(wapiPlugin.aggregatedTrack.__tracks[0], 0, { forceExisting: true })", :playing="isPlaying", :tracker="self", :hidePlay="hidden")
		AudioTrackerExactLengthTool(ref="exactLengthTool", :track="editingTrack", :tracker="self")
		AudioTrackerShortcuts(ref="shortcuts")
</template>

<script>
	import VueTypes from 'vue-types';
	import $ from 'jquery';
	import _ from 'lodash';
	import axios from 'axios';
	import Promise from 'bluebird';
	import Loader from 'app-core/Loader.vue';
	import AudioTrackerRuler from './AudioTrackerRuler.vue';
	import audioTrackerPlayerMixin from './audioTrackerPlayer';
	import audioTrackerEffectsMixin from './audioTrackerEffects';
	import AudioTrackInfoPanel from './AudioTrackInfoPanel.vue';
	import AudioTrackerTools from './AudioTrackerTools.vue';
	import AudioTrackerRecorderPopup from './AudioTrackerRecorderPopup.vue';
		import resizeObserver from 'resize-observer';
	import DxScrollView from 'devextreme-vue/scroll-view';
	import AudioTrackerMasterTrackHeadPanel from './AudioTrackerMasterTrackHeadPanel.vue';
	import AudioTrackerCanvas from './AudioTrackerCanvas.vue';
	import AudioTrackerButton from './AudioTrackerButton.vue';
	import AudioTrackerButtonGroup from './AudioTrackerButtonGroup.vue';
	import AudioTrackerMultitrackSelection from './AudioTrackerMultitrackSelection.vue';
	import WAPIPluginPopup from 'view/audioTracker/plugins/WAPIPluginPopup.vue';
	import { Container, Draggable } from 'vue-smooth-dnd';
	import audioHelper from './audio-helper';
	import AudioTrackSubTracksFilterPanel from './AudioTrackSubTracksFilterPanel.vue';
	import AudioTrackerVideoPlayer from './AudioTrackerVideoPlayer.vue';
	import videoHelper from './video-helper';
	import audioTrackerVideoMixin from './audioTrackerVideo';
	import AudioTrackerExactLengthTool from './AudioTrackerExactLengthTool.vue';
	import AudioTrackerTrackOverlay from './AudioTrackerTrackOverlay.vue';
	import AudioTrackerShortcuts from './AudioTrackerShortcuts.vue';
	import audioTrackerCopyPasteMixin from './audioTrackerCopyPaste.js';
	import audioTrackerRegionSelectionMixin from './audioTrackerRegionSelection';
	import AudioTrackerRegionSelectionUI from './AudioTrackerRegionSelectionUI.vue';
	import audioTrackerPlaybackFunctionsMixin from './audioTrackerPlaybackFunctions';
	import { initSoundTouchNode } from './plugins/soundTouchNode.js';

	export default {
		translationCategory: 'audioTracker.AudioTracker',
		name: 'audio-tracker',
		components: {
			Loader, AudioTrackerRuler, AudioTrackInfoPanel, AudioTrackerTools, AudioTrackerRecorderPopup, AudioTrackerButton, AudioTrackerButtonGroup,
			DxScrollView, AudioTrackerMasterTrackHeadPanel, AudioTrackerCanvas,
			AudioTrackerMultitrackSelection, WAPIPluginPopup, Container, Draggable, AudioTrackSubTracksFilterPanel, AudioTrackerVideoPlayer, AudioTrackerExactLengthTool,
			AudioTrackerTrackOverlay, AudioTrackerShortcuts, AudioTrackerRegionSelectionUI
		},
		mixins: [audioTrackerPlayerMixin, audioTrackerEffectsMixin, audioTrackerVideoMixin, audioTrackerCopyPasteMixin, audioTrackerRegionSelectionMixin, audioTrackerPlaybackFunctionsMixin],
		props: {
			items: VueTypes.array.def([]),
			aggregatedTracks: VueTypes.array.def([]),
			loading: VueTypes.any.def(null),
			loadingText: VueTypes.string.def(null),
			disabled: VueTypes.any.def(false),
			editingTrack: VueTypes.any.def(null),
			finalAudioName: VueTypes.string.def('FinalAudio'),
			masterTrackConfig: VueTypes.any.def({}),
			panelVisible: VueTypes.bool.def(true),
			allowPlay: VueTypes.bool.def(true),
			readonly: VueTypes.bool.def(false),
			subTracksVisibleLimit: VueTypes.number.def(5),
			isPublishMode: VueTypes.bool.def(false),
			preventKeyHandles: VueTypes.any.def(false),
			trackDragHandle: VueTypes.any.def(null),
			subInsertionsGroupingType: VueTypes.any.def('target'),
			language: VueTypes.any.def('en-US'),
			showLinksAlways: VueTypes.bool.def(false),
			isPublishing: VueTypes.bool.def(false),
			hidden: VueTypes.bool.def(false)
		},
		data() {
			var fadeTypes = ['exponential', 'linear'];
			return {
				isLoading: 0,
				isInitialized: false,
				display: {
					pixelsPerSecond: 50
				},
				tracks: [],
				soloTrackId: null,
				soloSubTrackId: null,
				soloSubTrackIds: [],
				tracksBase64: {},
				tracksBuffers: {},
				tracksEffects: {},
				tracksCanvasImages: {},
				audioContext: null,
				sources: {},
				fadeTypes,
				activeTrack: null,
				activeTrackInit: {},
				activeTrackNumberChanged: false,
				activeCanvas: null,
				activeTrackChanged: false,
				activeTrackMouseDownPosition: [],
				changedTracks: {},
				toolsTrack: null,
				activeToolId: null,
				isTracksActive: true,
				activeCanvasAction: null,
				activeCanvasMouseOverAction: null,
				bottomSpace: 0,
				selectedTrack: null,
				selectedMainTrack: null,
				navigatorPanelMouseDown: false,
				tracksListIds: null,
				focusedAggregatedTrack: null,
				isMultitrackActive: false,
				selectedTrackIds: [],
				selectedTracksHasSameAggregatedTrack: false,
				wapiPlugin: {
					aggregatedTrack: null
				},
				tracksListProp: [],
				aggregatedTracksActivePlayingItems: {},
				trackListPropHeights: {}
			};
		},
		computed: {
			visibleTracks() {
				return this.tracks.filter(f => f.__isVisible);
			},
			totalDuration() {
				var max = 0;
				this.visibleTracks.map(f => {
					max = Math.max(max, (f.start || 0) + (f.getTrimmedDuration() || 0));
				});
				return max;
			},
			totalVisibleDuration() {
				return parseFloat(this.totalDuration).toFixed(1);
			},
			self() {
				return this;
			},
			aggregatedTracksKeys() {
				return this.aggregatedTracks.map(f => f.trackNumber);
			},
			trackWidth() {
				return this.getTrackWidth();
			},
			visibleTimeArea: {
				get: function() {
					const start = Math.max(0, this.$refs.navigatorScroll?.instance?.scrollLeft && this.$refs.navigatorScroll?.instance?.scrollLeft() / this.display.pixelsPerSecond);
					const duration = this.$refs.navigatorScroll?.$el?.offsetWidth / this.display.pixelsPerSecond;
					return {
						start,
						end: start + duration,
						duration,
						width: this.$refs.navigatorScroll?.$el?.offsetWidth,
						pixelPerSecond: this.display.pixelsPerSecond
					}
				},
				cache: false
			}
		},
		watch: {
			aggregatedTracks: {
				handler() {
					this.updateTracksList(true);
				},
				deep: true
			},
			selectedTrackIds() {
				this.selectedTracksHasSameAggregatedTrack = Object.keys(_.groupBy(this.selectedTracks, v => v.trackNumber())).length === 1;
			},
			'items'(v) {
				this.setTracks(v);
			},
			totalDuration(v) {
				this.$emitTotalDurationChanged();
			},
			selectedMainTrack(v) {
				if (v)
					this.focusedAggregatedTrack = v?.__aggregatedTrack;
				this.updateTracksList(true);
				this.activeCanvasAction = null;
			},
			masterTrackConfig: {
				async handler() {
					await this.restoreTrackEffects(this.masterTrackConfig, this.masterTrackEffects);
				},
				deep: true
			},
			visibleTracks() {
				this.$loadData();
			},
			focusedAggregatedTrack(v) {
				if (this.selectedMainTrack?.trackNumber() !== v?.trackNumber) {
					this.selectedMainTrack = null;
				}
				this.updateTracksList(true);
			},
			tracks() {
				this.updateTracksList(true);
			},
			isLoading(v) {
				this.$emit('loading', v);
			},
			aggregatedTracksActivePlayingItems: {
				handler(v) {
					if (__DEBUG__)
						console.debug('aggregatedTracksActivePlayingItems chganged', JSON.stringify(v));
				},
				deep: true
			},
			'display.pixelsPerSecond'() {
				this.lastTimeChanged(this.lastTime);
			},
			isPlaying(v) {
				this.$emit('play-state', v);
			}
		},
		mounted() {
			if (__DEBUG__) {
				window.tracker = this;
				window.$ = $;
			}
			this.$emitTracksChanged = _.debounce(() => {
				if (__DEBUG__)
					console.debug('On track data changed');
				var changedTracks = this.tracks.filter(f => this.changedTracks[f.id]);
				if (changedTracks.length)
					this.$emit('tracks-changed', changedTracks, () => this.changedTracks = {});
			}, 1000, {
				'leading': false,
				'trailing': true
			});
			this.$emitTotalDurationChanged = _.debounce((v) => {
				if (__DEBUG__)
					console.debug('Total duration changed in audio tracker');
				this.$emit('total-duration-changed', this.totalDuration);
			}, 1000, {
				'leading': false,
				'trailing': true
			});
			this.$refreshPlay = _.debounce(this.refreshPlay, 100, {
				'leading': false,
				'trailing': true
			});
			this.$loadData = _.debounce(this.loadData, 100, {
				'leading': false,
				'trailing': true
			});
			this.$calculateAggregatedTracksActivePlayingItems = _.debounce(this.calculateAggregatedTracksActivePlayingItems, 20, {
				'leading': false,
				'trailing': true,
				'maxWait': 100
			});

			this.init();
			this.$update = _.debounce(() => {
				if (this._isDestroyed) return;
				this.$forceUpdate()
			}, 300, {
				'leading': false,
				'trailing': true,
				'maxWaith': 1000
			});
			this.$unobserve = resizeObserver.observe(this, this.$el, () => {
				if (this.$update)
					this.$update();
			});
			this.$unobserveWindow = resizeObserver.observe(this, this.$refs.resizeHandler, () => {
				if (this.$update)
					this.$update();
			});
			this.updateTracksList();

			this.$eventsBus.$on('update-tracker', this.updateTracksList);
		},
		beforeDestroy() {
			this.stop();
		},
		destroyed() {
			this.$eventsBus.$off('update-tracker', this.updateTracksList);			
			if (this.$unobserve)
				this.$unobserve();
			if (this.$unobserveWindow)
				this.$unobserveWindow();
			delete this.$unobserve;
			delete this.$refreshPlay;
			delete this.$emitTotalDurationChanged;
			delete this.$emitTracksChanged;
			delete this.$loadData;
			delete this.$update;
			delete this.$audioRecordedHandler;
			delete this.$channelData;
		},
		updated() {
			this.lastTimeChanged(this.lastTime);
		},
		methods: {
			getBufferChannelData(buffer, channel) {
				if (!this.$channelData) this.$channelData = [];
				let info = this.$channelData.find(f => f.buffer === buffer);
				if (!info) {
					info = {
						buffer,
						data: {}
					};
					this.$channelData.push(info);
				}
				if (!info?.data[channel]?.length) {
					info.data[channel] = buffer.getChannelData(channel);
				}
				return info.data[channel];
			},
			setFocusedAggregatedTrack(aggTrack) {
				if (aggTrack.__type !== 'video') {
					this.focusedAggregatedTrack = aggTrack;
				}
			},
			onAudioRecorded(wav, title, startPosition, trimSettings) {
				if (this.$audioRecordedHandler) {
					this.$audioRecordedHandler(wav, title, startPosition, trimSettings);
					delete this.$audioRecordedHandler;
				} else {
					this.$emit('new-audio-recorderd', wav, title, startPosition, trimSettings);
				}
			},
			getTrackWidth() {
				return Math.max(this.$refs.navigatorScroll?.$el?.offsetWidth - 6, (this.totalDuration + 1) * this.display.pixelsPerSecond - 4 + ((this.refsTools()?.$el?.offsetWidth + 10) || 0));
			},
			getCurrentPlayingEffects(trackKey) {
				var track = this.tracks.find(f => f.id === this.aggregatedTracksActivePlayingItems[trackKey]);
				return {
					track,
					effects: this.tracksEffects[track?.id]
				}
			},
			calculateAggregatedTracksActivePlayingItems() {
				this.tracksListProp.map(f => {
					var track = this.getActiveAggregatedTrackByPlayPosition(f.key, f.track, f.tracks);
					if (track?.id !== this.aggregatedTracksActivePlayingItems[f.key]) {
						this.$set(this.aggregatedTracksActivePlayingItems, f.key, track?.id);
						if (__DEBUG__)
							console.debug('Set aggregatedTracksActivePlayingItems', track?.id);
					}
				});

				this.calculateActiveVideoTrack();
			},
			scrollBy(delta) {
				const inst = this.$refs.navigatorScroll?.instance;
				if (!inst) return;

				const offset = inst.scrollOffset();

				offset.left += delta?.left || 0;
				offset.top += delta?.top || 0;
				
				inst.scrollTo(offset);
			},
			onInfoPanelsMouseWheel(e) {
				if (__DEBUG__)
					console.debug('onInfoPanelsMouseWheel', { e });
				const scroller = this.$refs.navigatorScroll?.instance;
				if (scroller && scroller.scrollHeight() !== scroller.clientHeight()) {
					this.scrollBy({ top: e.originalEvent.deltaY });
				}
			},
			onToolChanged(toolId) {
				this.activeToolId = toolId;

				setImmediate(() => {
					if (this._isDestroyed) return;
					if (this.$refs.navigatorScroll?.instance)
						this.$refs.navigatorScroll?.instance?.update();
					setImmediate(() => {
						let refTools = this.refsTools();
						let track = refTools?.$el?.closest('.audio-tracker-track');
						if (!refTools?.$el || !track) return;
						let toolsLeft = track.offsetLeft + track.offsetWidth;
						let toolsWidth = refTools.$el?.offsetWidth;
						let toolsRight = toolsLeft + toolsWidth + 40;
						let scrollOffset = this.$refs.navigatorScroll?.instance?.scrollOffset();
						let scrollOffsetRight = scrollOffset.left + this.$refs.navigatorScroll?.$el?.offsetWidth;
						if (toolsRight > scrollOffsetRight) {
							this.$refs.navigatorScroll?.instance?.scrollTo({
								top: scrollOffset.top,
								left: scrollOffset.left + (toolsRight - scrollOffsetRight)
							})
						}

						if (__DEBUG__)
							console.debug('Tools changed', { toolId, toolsRight, toolsWidth, toolsLeft, scrollOffsetRight, scrollOffset });
					});
				});
			},
			getMouseOverAggregatedTrack() {
				return this.$mouseOverAggregatedTrack;
			},
			setMouseOverAggregatedTrack(id) {
				this.$mouseOverAggregatedTrack = id;
			},
			lastTimeChanged(v) {
				//OPTIMIZATION FOR TIMELINE RENDERING
				const position = { left: `${v * this.display.pixelsPerSecond}px` };
				$(this.$refs.currentTime).css(position);
				$(this.$refs.rulerCurrentTime)
					.css(position)
					.text(this.$Data.secondsToDisplayValue(v));
				this.$calculateAggregatedTracksActivePlayingItems();
				this.calculateNavigatorPositionByCurrentTime();
			},
			calculateNavigatorPositionByCurrentTime(force, byTimeline) {
				if (this.$refs.navigatorScroll?.instance && (this.isPlaying || force)) {
					const timeInPixels = this.currentTime * this.display.pixelsPerSecond;
					const offset = this.$refs.navigatorScroll?.instance?.scrollOffset();
					const navWidth = this.$refs.navigatorScroll?.$el?.offsetWidth;

					if (byTimeline) {
						this.scrollBy({ left: timeInPixels - offset.left - navWidth / 2 });
						return;
					}

					const width = this.$refs.navigatorScroll?.$el?.offsetWidth;
					let delta = 0;
					if (timeInPixels < offset.left) {
						delta = -width;
					} else if (timeInPixels > (offset.left + width)) {
						delta = width;
					}
					if (delta) {
						this.scrollBy({ left: delta });
					}
				}
			},
			isVisibleInNavigator(track, trackLineId) {
				if (!this.$refs?.navigatorScroll?.$el) return false;
				trackLineId = trackLineId || `trackLine_${track?.__aggregatedTrack?.trackNumber}`;
				const navigatorHeight = this.$refs.navigatorScroll?.$el?.offsetHeight / 2;
				const navigatorWidth = this.$refs.navigatorScroll?.$el?.offsetWidth / 2;
				const trackLine = this.$refs[trackLineId] && this.$refs[trackLineId][0];
				if (!trackLine) return false;
				const trackCanvasOffsets = {
					x: track.start * this.display.pixelsPerSecond,
					y: trackLine?.offsetTop,
					width: track.getTrimmedDuration() * this.display.pixelsPerSecond,
					height: trackLine?.offsetHeight
				};
				const navPointStart = { x: this.$navigatorScrollOffset?.left || 0, y: this.$navigatorScrollOffset?.top || 0 };
				const navPointEnd = { x: (this.$navigatorScrollOffset?.left || 0) + navigatorWidth, y: (this.$navigatorScrollOffset?.top || 0) + navigatorHeight };
				const canvasPointStart = { x: trackCanvasOffsets.x, y: trackCanvasOffsets.y };
				const canvasPointEnd = { x: trackCanvasOffsets.x + trackCanvasOffsets.width, y: trackCanvasOffsets.y + trackCanvasOffsets.height };

				const result =
					canvasPointStart.x <= navPointEnd.x && canvasPointEnd.x >= navPointStart.x &&
					canvasPointStart.y <= navPointEnd.y && canvasPointEnd.y >= navPointStart.y;

				if (__DEBUG__)
					console.debug('IsVisibleInNavigator2', result, { navigatorScrollOffset: this.$navigatorScrollOffset, trackLineId, trackCanvasOffsets, navPointStart, navPointEnd, canvasPointStart, canvasPointEnd });

				return result;
			},
			onTrackInfoPanelDrop(e) {
				var movingItem = this.tracksListProp[e.removedIndex];
				if (movingItem?.isAggregatedTrack) {
					var movingAggTrack = movingItem.aggregatedTrack;
					var nextAggregatedTrack = this.tracksListProp.filter((f, i) => f.isAggregatedTrack && i >= e.addedIndex && f.trackNumber !== movingAggTrack.trackNumber)[0]?.aggregatedTrack;
					var currentIndex = _.findIndex(this.aggregatedTracks, v => v.trackNumber === movingAggTrack.trackNumber);
					var newIndex = nextAggregatedTrack ? _.findIndex(this.aggregatedTracks, v => v.trackNumber === nextAggregatedTrack.trackNumber) : this.aggregatedTracks.length + 1;
					if (__DEBUG__)
						console.debug('On track drop', { e, movingAggTrack, nextAggregatedTrack, currentIndex, newIndex });
					if (currentIndex > -1 && newIndex > -1 && currentIndex !== newIndex)
						this.$emit('aggregated-track-reorder', movingAggTrack, currentIndex, newIndex);
				}
			},
			showWAPIPlugin(aggTrack, pluginId) {
				this.wapiPlugin.aggregatedTrack = aggTrack;
				this.$refs.wapiPlugin?.showPopup(pluginId, void 0, void 0, aggTrack);
			},
			showWAPIPluginInstance(aggTrack, pluginId, instance) {
				this.wapiPlugin.aggregatedTrack = aggTrack;
				this.$refs.wapiPlugin?.showPopup(pluginId, instance, instance?.instanceId, aggTrack);
			},
			refsTools() {
				return this.$refs?.tools && this.$refs?.tools[0];
			},
			setSoloTrack(trackListItem) {
				if (__DEBUG__)
					console.debug('SetSoloTrack');
				var key = trackListItem?.key;
				if (trackListItem.isAggregatedTrack) {
					this.soloTrackId = this.soloTrackId === key ? null : key;
					this.$emit('set-solo-aggregated-track', this.soloTrackId);
				} else {
					this.soloSubTrackId = this.soloSubTrackId === key ? null : key;
					this.soloSubTrackIds = this.soloSubTrackId ? trackListItem.tracks.map(f => f.id) : [];
					this.$emit('set-solo-sub-tracks', trackListItem?.tracks);
				}
				this.$refreshPlay();
			},
			getActiveAggregatedTrackByPlayPosition(aggregatedTrack, track, tracks) {
				tracks = tracks || this.tracks.filter(f => f.trackNumber() === aggregatedTrack);
				var resultTracks = tracks.filter(f => !f.__isSubTrack && this.currentTime >= f.start && this.currentTime <= (f.start + f.getTrimmedDuration()));
				if (resultTracks.length)
					return resultTracks[0];

				resultTracks = tracks.filter(f => !f.__isSubTrack && this.currentTime >= f.start);
				if (resultTracks.length)
					return _.last(resultTracks);

				resultTracks = _.orderBy(tracks.filter(f => !f.__isSubTrack), v => v.start);

				if (resultTracks.length)
					return _.first(resultTracks);

				if (track)
					return track;

				return {
					trackNumber: aggregatedTrack,
					__aggregatedTrack: this.aggregatedTracks?.filter(f => f?.trackNumber === aggregatedTrack)[0]
				}
			},
			tracksList() {
				let arr = this.aggregatedTracksKeys.reduce((arr, aggTrack) => {
					let tracks = this.getAggregatedTrackItems(aggTrack);
					let aggregatedTrack = this.aggregatedTracks.filter(f => f.trackNumber === aggTrack)[0];
					let track = tracks.filter(t => this.selectedMainTrack?.trackNumber() !== aggTrack || t.id === this.selectedMainTrack.id)[0] || {
						trackNumber: aggTrack,
						__aggregatedTrack: aggregatedTrack,
						insertion: {}
					};

					let effects = this.tracksEffects[track?.id];
					let item = {
						track,
						aggregatedTrack,
						key: aggTrack,
						effects,
						isAggregatedTrack: true
					};
					Object.defineProperty(item, 'tracks', {
						get: () => this.getAggregatedTrackItems(aggTrack)
					});
					arr.push(item);

					if (this.focusedAggregatedTrack?.trackNumber === aggTrack && this.selectedMainTrack) {
						const mainTracks = [this.selectedMainTrack];
						mainTracks.push(...(this.focusedAggregatedTrack?.__tracks?.filter(f => f.id !== this.selectedMainTrack.id) || []));

						const subTrackGroups = [];
						const addGroups = (groups, isRoot) => {
							groups.map(group => {
								var existing = subTrackGroups.filter(f => f.groupKey === group.groupKey)[0];
								var added = false;
								if (!existing) {
									if (isRoot) {
										subTrackGroups.push(group);
										added = true;
									}
								} else {
									existing.tracks.push(...group.tracks);
									added = true;
								}
								if (added)
									group.tracks.map(f => f.__subTrackGroup = group.groupKey);
							})
						};

						mainTracks.map((selectedMainTrack, trackIdx) => {
							var subTracks = selectedMainTrack?.subTracks;
							selectedMainTrack.__initSubTracks();
							if (subTracks?.length) {
								subTracks.map(f => {
									f?.__ensureTrackLoaded && f.__ensureTrackLoaded();
								});
								var groupedSubTracks = _.groupBy(subTracks, f => {
									if (this.subInsertionsGroupingType === 'target') {
										return f?.insertion?.eventRuleId || f?.insertion?.Targets?.length ? JSON.stringify({
											rule: f?.insertion?.eventRuleId,
											geoTargets: f?.insertion?.Targets?.map(f => `${f.LinkedTarget.Oid}_${f.Exclude}`).join(',')
										}) : ''
									} else {
										return f?.insertion?.eventRuleId || f?.insertion?.Targets?.length ? JSON.stringify({
											rule: f?.insertion?.eventRuleId,
											name: f?.insertion?.Name
										}) : ''
									}
								});
								var groupIdx = 0;
								for(var group in groupedSubTracks) {
									var groupTracks = groupedSubTracks[group];
									if (group) {
										var groupTrack = groupTracks[0];
										addGroups([{
											tracks: groupTracks,
											track: groupTrack,
											aggregatedTrack,
											key: `${aggTrack}_subTrack_${group}`,
											groupKey: `${aggTrack}_subTrack_${group}`,
											effects,
											isAggregatedTrack: false,
											groupIdx
										}], trackIdx === 0);
									} else {
										addGroups(groupTracks.map((f, i) => ({
											tracks: [f],
											track: f,
											aggregatedTrack,
											key: `${aggTrack}_subTrack_${f?.id || (groupIdx + i)}`,
											groupKey: `${aggTrack}_subTrack_${groupIdx + i}`,
											effects,
											isAggregatedTrack: false,
											groupIdx
										})), trackIdx === 0);
										groupIdx += groupTracks.length;
									}
								}
								selectedMainTrack.__groups = subTrackGroups;
							}
						});

						if (__DEBUG__)
							console.debug('Tracks list', { mainTracks, subTrackGroups });

						arr.push(...subTrackGroups);
					}
					return arr;
				}, []);

				var tracksListIds = arr.filter(f => f.key).map(f => f.key).join(',');
				if (this.tracksListIds !== tracksListIds) {
					this.tracksListIds = tracksListIds;
				}

				return arr;
			},
			updateTracksList(force) {
				const tracksList = this.tracksList();
				const getKey = (f) => `${f.key}_${f.tracks.map(t => t.id).join('_')}`;
				if (force || tracksList.map(f => getKey(f)).join() !== this.tracksListProp.map(f => getKey(f)).join()) {
					this.tracksListProp = tracksList;
					this.updateNavigationScroll();
				}
			},
			isTrackSelected(track) {
				return this.selectedMainTrack?.id === track.id || this.selectedTrack?.id === track.id || this.selectedTrackIds.indexOf(track.id) > -1;
			},
			getAggregatedTrackItems(aggregatedTrack, all) {
				return this.tracks.filter(f => f.trackNumber() === aggregatedTrack && (all || !f.__isSubTrack));
			},
			getAggregatedTrackOpts(aggregatedTrack) {
				const track = this.getAggregatedTrackItems(aggregatedTrack)[0];
				return {
					track: track || {},
					effects: this.tracksEffects[track?.id] || {}
				}
			},
			isAggregatedTrackLoading(aggregatedTrack) {
				return this.getAggregatedTrackItems(aggregatedTrack)?.some(f => !f.__isSrcLoaded || f.__isLoading);
			},
			onNavigatorScroll({ scrollOffset }) {
				if (scrollOffset?.left != this.$navigatorScrollOffset?.left || scrollOffset?.top != this.$navigatorScrollOffset?.top) {
					this.$navigatorScrollOffset =  { ...scrollOffset };
					this.$eventsBus.$emit('navigatorScrollOffset', scrollOffset);
					this.$refs.infoPanelsScroll?.instance?.scrollTo(scrollOffset);
					this.$refs.rulerScroll?.instance?.scrollTo({ left: scrollOffset?.left });
				}
			},
			canPlay(opts) {
				return !this.disabled && this.isTracksActive && (opts?.allowModal || !$('.dx-overlay-modal').length) && this.allowPlay && !this.toolsTrack && this.visibleTracks.length && !$(document.activeElement).is('input');
			},
			recordVoice(opts) {
				if (opts?.handler) {
					this.$audioRecordedHandler = opts?.handler;
				}
				this.$refs.recorder.showPopup(true, opts);
			},
			processRecording(v) {
				v = v || 'start';
				var recorder = this.$refs.recorder;
				return recorder && (v === 'pause' ? recorder.pauseRecording() : (v === 'start' ? recorder.startRecording() : recorder.stopRecording()));
			},
			setTracksActive(v) {
				this.isTracksActive = v;
			},
			getNavigatorWidth(delta) {
				return $(window).width() - (this.$refs.navigatorScroll && $(this.$refs.navigatorScroll?.$el).offset().left) - (delta || 0);
			},
			getNavigatorHeight(delta) {
				return Math.max(300, $(window).height() - (this.$refs.navigatorScroll && $(this.$refs.navigatorScroll?.$el).offset().top) - (delta || 0)) - 5;
			},
			initSystemTrackInfo(f) {
				const addProp = (prop, def) => {
					if (!f.hasOwnProperty(prop))
						f[prop] = def;
				};

				addProp('__isSrcLoading', false);
				addProp('__loadedPercentage', 0);
				addProp('__isLoading', 0);

				if (!f.hasOwnProperty('__isVisible')) {
					Object.defineProperty(f, '__isVisible', {
						get: () => {
							return f.allowPublishing === true || this.soloSubTrackIds?.includes(f.id) || !f.__isSubTrack || this.tracksListProp.some(t => t?.tracks?.some(tr => tr.id === f.id));
						}
					});
				}

				if (!f.hasOwnProperty('__getChannelData')) {
					Object.defineProperty(f, '__getChannelData', {
						get: () => {
							return this.getBufferChannelData
						}
					});
				}

				if (!f.hasOwnProperty('__preProcessSourceBuffer')) {
					let preProcessSourceBuffer = () => {
						if (this.tracksBuffers[`${f?.id}_source`] && f?.__isSrcLoaded && !f?.__isPreProcessing) {
							if (__DEBUG__)
								console.debug('preProcessSourceBuffer');
							f.__isPreProcessing = true;
							this.$nextTick(() => {
								this.setBuffer(f.id, this.tracksBuffers[`${f?.id}_source`]);
							});
						}
					}

					Object.defineProperty(f, '__preProcessSourceBuffer', {
						get: () => preProcessSourceBuffer
					});
				}

				if (!f.hasOwnProperty('__buffer')) {
					Object.defineProperty(f, '__buffer', {
						get: () => {
							return this.tracksBuffers[f.id];
						}
					});
				}

				if (!f.hasOwnProperty('__emitDataChanged')) {
					Object.defineProperty(f, '__emitDataChanged', {
						get: () => {
							return (logItemCategory) => this.onTrackDataChanged(f, logItemCategory);
						}
					});
				}

				if (!f.hasOwnProperty('__isSrcLoaded')) {
					let __isSrcLoaded = false;
					Object.defineProperty(f, '__isSrcLoaded', {
						get: () => __isSrcLoaded,
						set: (v) => {
							__isSrcLoaded = v;
							if (v) {
								f.__isSrcLoading = false;
								f.__loadedPercentage = 100;
							}
						},
						enumerable: true
					});
				}

				if (!f.hasOwnProperty('__tracker')) {
					let tracker = this;
					Object.defineProperty(f, '__tracker', {
						get: () => tracker,
						set: (v) => tracker = v
					});

					const setBase64ForTrack = (base64, emitChange) => this.setBase64ForTrack(f, base64, emitChange);
					Object.defineProperty(f, '__setBase64', {
						get: () => setBase64ForTrack
					});
					Object.defineProperty(f, '__base64', {
						get: () => this.tracksBase64[f.id]
					});
					
					let getInfoPanel = () => (tracker.$refs[`trackInfo_${f.__aggregatedTrack?.trackNumber}`] || [])[0];
					Object.defineProperty(f, '__infoPanel', {
						get: () => getInfoPanel
					})
				}

				if (!f.hasOwnProperty('__canvasHeight')) {
					Object.defineProperty(f, '__canvasHeight', {
						get: () => (this.trackListPropHeights[this.getTrackListItem(f.id)?.key] - 10) || 0
					});
				}

				if (!f.hasOwnProperty('__overlappedByTracks')) {
					Object.defineProperty(f, '__overlappedByTracks', {
						get: () => this.getOverlappedByTracks(f.id)
					});

					Object.defineProperty(f, '__overlappingTracks', {
						get: () => {
							if (f.__isSubTrack) return [];
							const trackStart = parseFloat(f.start.toFixed(2));
							return this.tracks.filter(t => t.trackNumber() === f?.trackNumber() && !t.__isSubTrack && t.id !== f?.id &&
								parseFloat(t.start.toFixed(2)) < trackStart && parseFloat((t.start + t.getTrimmedDuration()).toFixed(2)) > trackStart);
						}
					})
				}

				if (!f.hasOwnProperty('__trackLineTop')) {
					const getTrackLine = () => {
						var trackLineId = f?.__isSubTrack ? this.getTrackListItem(f?.id)?.key : f?.__aggregatedTrack?.trackNumber;
						var trackLine = this.getRefArrayElem(`track_${trackLineId}`);
						return trackLine;
					}

					Object.defineProperty(f, '__trackLineTop', {
						get: () => getTrackLine()?.offsetTop
					})

					Object.defineProperty(f, '__trackLineHeight', {
						get: () => getTrackLine()?.offsetHeight
					});
				}

				videoHelper.defineVideoProperties(f);

				return f;
			},
			getOverlappedByTracks(trackId) {
				const track = this.tracks.filter(f => f.id === trackId)[0];
				if (!track || track.__isSubTrack) return [];
				const trackStart = track.start;
				const trackEnd = track.start + track.getTrimmedDuration();
				return this.tracks.filter(t => t.trackNumber() === track?.trackNumber() && !t.__isSubTrack && t.id !== track?.id &&
					t.start < trackEnd && (t.start + t.getTrimmedDuration()) > trackStart && t.start > trackStart);
			},
			setTracks(arr) {
				if (__DEBUG__)
					console.debug('Set tracks', arr);
				_.remove(this.tracks, f => !_.find(arr, (v) => v.id === f.id ));
				this.tracks.push(...arr.filter(v => !_.find(this.tracks, (f) => v.id === f.id)).map(f => this.initSystemTrackInfo(f)));

				arr.map(f => {
					var track = this.tracks.filter(t => t.id === f.id)[0];
					if (track) {
						track.__sortIndex = f.__sortIndex;
					}
				});
				this.tracks.sort((a,b) => a.__sortIndex - b.__sortIndex);

				if (this.selectedMainTrack && !this.tracks.filter(f => f.id === this.selectedMainTrack?.id)[0]) {
					this.selectedMainTrack = null;
				}

				this.$loadData();
			},
			checkToolsTrackAction(e, action) {
				var time = this.getTimeByEvent(e);
				return this.refsTools()?.processTrackAction(time, action);
			},
			onTrackMouseDown(e, track, item) {
				if (__DEBUG__)
					console.debug('On track mouse down', { e, track, item });
				if (this.toolsTrack && this.checkToolsTrackAction(e, 'mouse-down')) return;

				this.$mouseOverAggregatedTrack = track?.__aggregatedTrack?.trackNumber;

				if (!this.$mouseOverTrack && (!item || item.isAggregatedTrack === true)) {
					this.selectedTrack = null;
					this.activeTrack = null;
					this.selectedMainTrack = null;
					if (!this.$refs.multitrackSelector?.isShiftPressed) {
						this.selectedTrackIds = [];
					}
				}
				this.activeTrackMouseDownPosition = [e.clientX, e.clientY];
			},
			onTrackMouseUp(e, track) {
				if (this.toolsTrack && this.checkToolsTrackAction(e, 'mouse-up')) return;
				this.activeTrackMouseDownPosition = null;
				this.activeCanvasAction = null;
			},
			onTrackClick(e) {
				if (__DEBUG__)
					console.debug('Track click');

				if (this._isDestroyed) return;

				var handled = false;
				if (this.toolsTrack)
					handled = this.checkToolsTrackAction(e, 'click');

				if (handled) return;

				var time = this.getTimeByEvent(e);
				if (this.isPlaying) {
					this.play(time);
				} else {
					this.currentTime = time;
				}
			},
			roundToClosest(v, len) {
				len = len || 2;

				let actLen = 0;
				let hasAnyNum = false;
				let hasDot = false;
				return parseFloat([...`${v || 0}`].map(f => {
					hasDot = hasDot || f.match(/[\.\,]/g);
					hasAnyNum = hasDot && (hasAnyNum || !f.match(/[0\.\,]/g));
					if (hasAnyNum) {
						actLen++;
						return actLen <= len ? f : '';
					} else {
						return f;
					}
				}).filter(f => f).join(''));
			},
			getTimeByEvent(e) {
				return ((e.clientX - $(this.$refs.navigator).offset()?.left || 0) + $(this.$refs.navigator).scrollLeft()) / this.display.pixelsPerSecond;
			},
			zoom(multiplier) {
				this.display.pixelsPerSecond = this.roundToClosest(this.display.pixelsPerSecond * multiplier, 1);
				if (this.display.pixelsPerSecond > 15) {
					this.display.pixelsPerSecond = Math.round(this.display.pixelsPerSecond / 10) * 10;
				}

				var timePosition = this.lastTime * this.display.pixelsPerSecond;
				var scrollPosition = timePosition - $(this.$refs.navigatorScroll?.$el).width() / 2;

				if (__DEBUG__)
					console.debug('Zoom', { timePosition, scrollPosition, multiplier });
				this.$nextTick(() => {
					if (this._isDestroyed) return;
					const scroll = this.$refs.navigatorScroll && this.$refs.navigatorScroll.instance;
					if (scroll) {
						scroll.scrollTo({
							left: Math.max(0, scrollPosition),
							top: scroll.scrollTop()
						});
					}
				});
			},
			makeRoundButton(element) {
				$(element).removeClass('dx-button-has-text').addClass('dx-button-has-icon');
			},
			isTrackSrcLoaded(track) {
				return track && track.__isSrcLoaded;
			},
			updateNavigationScroll() {
				setImmediate(() => {
					if (this._isDestroyed) return;
					if (this.$refs.navigatorScroll)
						this.$refs.navigatorScroll.instance.update();
				});
			},
			init() {
				$(this.$refs.infoPanelsScroll?.$el).on('mousewheel', this.onInfoPanelsMouseWheel);
				$(this.$el).on('mousewheel', e => {
					if (e.ctrlKey) {
						var value = e.originalEvent.deltaY < 0 ? 1.5 : 1/1.5;
						this.zoom(value);
						e.preventDefault();
						e.stopPropagation();
					} else  {
						var direction = e.originalEvent.deltaY < 0 ? -1 : 1;
						var track = this.selectedMainTrack;
						if (track?.__totalSubInsertions && track?.addSubTracksSkip && track?.addSubTracksSkip(this.subTracksVisibleLimit * direction)) {
							this.updateTracksList(true);
							e.preventDefault();
							e.stopPropagation();
						}
					}
				});

				this.tracksBase64 = {};

				this.audioContext = new AudioContext({
					latencyHint: 'interactive',
					sampleRate: 48000,
				});

				if (this.items)
					this.setTracks(this.items);

				this.initMasterTrack();

				Object.defineProperty(this, 'selectedTracks', {
					get: () => {
						return this.selectedTrackIds.length ? this.tracks.filter(f => this.selectedTrackIds.some(v => v === f.id)) : (this.selectedTrack ? [this.selectedTrack] : []);
					}
				});
			},
			async getSrc(src, track, onProgress) {
				var progressEvent = (ev) => {
					track.__loadedPercentage = parseInt(100 * ev.loaded / ev.total);
					if (onProgress) {
						onProgress(ev);
					}
				};

				switch(typeof(src)) {
					case 'function':
						return new Promise(resolve => src(false, progressEvent).then(dataUrl => {
							if (!dataUrl) return resolve({ data: { size: 0 } });
							axios.get(dataUrl, {
								responseType: 'blob'
							}).then(r => resolve(r));
							return null;
						}));
					default:
						return axios
							.get(src, {
								responseType: 'blob',
								onDownloadProgress: progressEvent
							})
				}
			},
			async processLoadedAudioBufferFixes(track, buffer) {
				let bufferDuration = parseInt(buffer.duration * 1000);

				if (__DEBUG__)
					console.debug('processLoadedAudioBufferFixes', { insDur: track.insertion.Duration, bufferDuration });

				if (Math.abs(track.insertion.Duration - bufferDuration) > 10) {
					let oldDuration = track.insertion.Duration;
					await track.__processLinkedTracks({ delta: (bufferDuration - track.insertion.Duration) / 1000, changed: 'end' }, true);
					track.insertion.Duration = bufferDuration;
					track.__saveInsertion(void 0, void 0, true);
					if (__DEBUG__)
						console.debug(`Fix duration for ${track.name()} ${oldDuration/1000} -> ${bufferDuration/1000}`);
				}
				if (track.duration === 0) {
					track.duration = buffer.duration;
					track.insertion.Duration = parseInt(buffer.duration * 1000);
					track.__saveInsertion(void 0, void 0, true);
				}
			},
			async processLoadedAudioBuffer(track, buffer) {
				if (!track || !buffer) return;

				if (__DEBUG__)
					console.debug('processLoadedAudioBuffer');

				let trackId = track?.id;
				if (this.tracksBuffers[trackId]) {
					this.$set(this.tracksBuffers, trackId, null);
				}

				this.$set(this.tracksBuffers, `${trackId}_source`, buffer);

				buffer = await this.preProcessBuffer(buffer, track, track?.__aggregatedTrack);

				if (__DEBUG__)
					console.debug('PrcessedBuffer', { buffer });

				this.$set(this.tracksBuffers, trackId, buffer);
				if (this.isPublishMode)
					delete this.tracksBase64[trackId];

				track.__isPreProcessing = false;
				
				await this.processLoadedAudioBufferFixes(track, buffer);

				return buffer;
			},
			prepareLoadedArrayBuffer(trackId, track) {
				if (this.tracksBuffers[trackId]) return Promise.resolve(this.tracksBuffers[trackId]);
				if (__DEBUG__)
					console.debug('prepareLoadedArrayBuffer');
				return new Promise((resolve) => {
					var dataUrl = this.tracksBase64[trackId];
					if (!dataUrl) {
						return Promise.resolve(null);
					}
					if (track?.__isAudioTrack) {
						return axios.get(dataUrl, {
							responseType: 'blob'
						}).then(r => {
							var fr = new FileReader();
							fr.readAsArrayBuffer(r.data);
							fr.onloadend = () => {
								this.audioContext.decodeAudioData(fr.result, async (buffer) => {
									buffer = await this.processLoadedAudioBuffer(track, buffer);
									resolve(buffer);
								},() => resolve(null));
							};
						});
					} else if (track.__isVideoTrack) {
						if (track.duration === 0) {
							if (__DEBUG__)
								console.debug('Calc video duration for track', { track })
							videoHelper.getVideoDuration(dataUrl).then(d => {
								track.duration = d;
								track.insertion.Duration = parseInt(d * 1000);
								track.__saveInsertion();
								return d;
							}).finally(() => resolve(null));
						} else {
							resolve(null);
						}
					} else resolve(null);
				});
			},
			async loadData(tracks) {
				if (this._isDestroyed) return;

				if (__DEBUG__)
					console.debug('Load data');
				
				const hasForcedTracks = !!tracks?.length;
				tracks = tracks || this.tracks;

				if (!tracks?.length) {
					this.isInitialized = true;
					return Promise.resolve([]);
				}
				
				var promises = tracks.filter(f => (f.__isVisible || f.allowPublishing === true || hasForcedTracks) && !f.__isSrcLoading && !f.__isSrcLoaded).map(f => new Promise((resolve) => {
					try {
						f.__isSrcLoading = true;
						return this.getSrc(f.src, f)
							.then(response => {
								if (__DEBUG__)
									console.debug('Src load response', { response });
								if (response.data.size === 0) {
									f.__isSrcLoaded = true;
									this.$emit('source-loaded', f);
									resolve(null);
									return null;
								}

								var fr = new FileReader();
								fr.readAsDataURL(response.data);
								fr.onloadend = (evt) => {
									this.tracksBase64[f.id] = evt.target.result;
									this.tracksCanvasImages[f.id] = null;

									this.prepareLoadedArrayBuffer(f.id, f).then(async buffer => {
										this.tracksEffects[f.id] = await this.getPipelineEffects(f.__aggregatedTrack);

										if (buffer && Math.abs((f.duration || 0) - (buffer.duration || 0)) > 0.1) {
											if (__DEBUG__)
												console.debug('Duration changed', { old: f.duration, new: buffer.duration });
											f.duration = buffer.duration;
										}

										f.__isSrcLoaded = true;
										this.tracksCanvasImages[f.id] = null;
										this.$emit('source-loaded', f);
										f.__isSrcLoading = false;
										f.__drawCanvas(true);
										resolve(buffer);
									}).catch(() => {
										f.__isSrcLoaded = true;
										f.__isSrcLoading = false;
										resolve(null);
									});

									return null;
								};
							})
							.catch(ex => {
								f.__isSrcLoaded = true;
								f.__isSrcLoading = false;
								f.__srcLoadError = ex && ex.ErrorMessage;
								resolve(null);
							});
					} catch {
						f.__isSrcLoaded = true;
						f.__isSrcLoading = false;
						resolve(null);
					}
				}));

				if (__DEBUG__)
					console.debug('Load data sources:', promises.length);

				if (promises.length) {
					this.isLoading++;
					return Promise.all(promises).finally(() => {
						if (this._isDestroyed) return;
						this.isLoading--;
						this.isInitialized = true;
						this.updateTracksList();
						return tracks;
					});
				} else {
					this.updateTracksList();
					if (!this.isInitialized) {
						this.isInitialized = true;
					}
					return Promise.resolve(tracks);
				}
			},
			getRefArrayElem(id) {
				return this.$refs[id] && this.$refs[id][0];
			},
			getTrackListItem(trackId) {
				return this.tracksListProp.filter(f => f.tracks.some(t => t?.id === trackId))[0];
			},
			drawTrack(itemKey) {
				var trackInfo = this.getRefArrayElem(`trackInfo_${itemKey}`)?.$el;
				this.$set(this.trackListPropHeights, itemKey, trackInfo?.offsetHeight);
				if (__DEBUG__)
					console.debug('Draw track', { itemKey, newHeight: this.trackListPropHeights[itemKey] });
			},
			setBase64ForTrack(track, base64, emitChange) {
				if (__DEBUG__)
					console.debug('SetBase64ForTrack');
				if (this.tracksBase64[track.id]) {
					track.__addTrackAudioToUndoHistory({ lastBase64: this.tracksBase64[track.id], nextBase64: base64 });
				}
				track.src = base64;
				track.__isSrcLoaded = false;
				this.tracksBase64[track.id] = base64;
				this.$set(this.tracksBuffers, track.id, null);
				this.$loadData();

				if (emitChange)
					this.$emit('audio-changed', track, base64);
			},
			async setBuffer(trackId, buffer, base64) {
				var track = this.tracks.find(f => f.id === trackId);

				if (!track) return;

				if (__DEBUG__)
					console.debug('setBuffer');

				if (this.tracksBase64[track.id]) {
					track.__addTrackAudioToUndoHistory({ lastBase64: this.tracksBase64[track.id], nextBase64: base64 });
				}
				
				if (base64)
					this.tracksBase64[trackId] = base64;

				buffer = await this.processLoadedAudioBuffer(track, buffer);
				setImmediate(async () => {
					if (__DEBUG__)
						console.debug(`Set buffer for ${trackId}`, { track, buffer, base64 });
					if (this._isDestroyed) return;
					if (track) {
						if (buffer?.duration)
							track.duration = buffer.duration;
						track.__isSrcLoaded = !!buffer;
						if (!this.tracksEffects[track.id])
							this.tracksEffects[track.id] = await this.getPipelineEffects(track);
					}
				});
			},
			async preProcessBuffer(buffer, track, aggTrack) {
				if (!buffer) return;

				if (__DEBUG__)
					console.debug('PreProcessBuffer'/*, { buffer, track, aggTrack }*/);

				buffer = await this.preProcessBufferAudio(track, buffer);
				
				track.__audioInfo = audioHelper.getBufferInfo(buffer);
				
				return buffer;
			},
			async preProcessBufferAudio(track, buffer) {
				let pitchFactor = track?.pitchRate || 1;
				let speedFactor = track?.playbackRate || 1;

				let haveProcessing = (pitchFactor !== 1 || speedFactor !== 1);
				if (haveProcessing) {
					track.__isLoading++;
					try {
						let targetTime = buffer.duration / speedFactor;
						let shifter = await initSoundTouchNode(this.audioContext);
						let buf = await shifter.processFullBuffer(buffer, {
							pitch: pitchFactor,
							tempo: speedFactor
						});
						
						if (__DEBUG__)
							console.debug(`preProcessBufferAudio ${pitchFactor} ${speedFactor}`, { pitchFactor, speedFactor, targetTime, bufTime: buf.duration });

						return buf;
					} finally {
						track.__isLoading--;
					}
				}
				return buffer;
			},
			onTrackVolumeChanged(track) {
				this.onTrackDataChanged(track, 'volume');
			},
			onTrackEffectsChanged(track) {
				this.onTrackDataChanged(track, 'effects');
			},
			onTrackDataChanged(track, changeLogItem) {
				if (__DEBUG__)
					console.debug(`%c${changeLogItem}`, 'background: orange; color: white', _.pick(track, ['__isSrcLoaded']));
				this.changedTracks[track.id] = true;
				this.$emitTracksChanged();
				if (track.__isRestoredFromUndo) {
					track.__isRestoredFromUndo = false;
					track.__skipAddToHistory = true;
				} else {
					track.__skipAddToHistory = false;
				}
			},
			applyTool(track, toolId, startPoint) {
				if (this.toolsTrack) {
					this.refsTools()?.cancelTool();
				}
				this.toolsTrack = track;
				setImmediate(() => {
					if (this._isDestroyed) return;
					this.refsTools()?.setTool(toolId, startPoint);
					setImmediate(() => {
						if (this._isDestroyed) return;
						this.$update();
					})
				});
			},
			getTrackAudioBase64(track) {
				return this.tracksBase64[track && track.id || track];
			},
			async onMasterTrackConfigChanged(config) {
				if (this.masterTrackEffects) {
					await this.restoreTrackEffects(config, this.masterTrackEffects);
				}
				this.$emit('master-track-config-changed', config);
			},
			getTrackInfo(track) {
				return this.getRefArrayElem(`trackInfo_${track?.__aggregatedTrack?.trackNumber}`);
			},
			onMouseDown(e) {
				const readonly = this.selectedTrack?.__aggregatedTrack?.__readonly;
				if (readonly) return;
				if (!($(e.target).is('.navigator-panel') || $(e.target).parents('.navigator-panel').length)) {
					this.navigatorPanelMouseDown = false;
				}
			},
			onMouseUp() {
				this.activeCanvasMouseOverAction = null;
			},
			onMouseMove(e) {
				if (this.rangeSelection.visible) return;

				this.$mouseMoveTimePosition = this.getTimeByEvent(e);
				if (this.toolsTrack && this.checkToolsTrackAction(e, 'mouse-move')) return;

				const readonly = this.selectedTrack?.__aggregatedTrack?.__readonly;

				if (!readonly && this.$mouseOverTrack) {
					if (this.$mouseOverTrack?.id === this.selectedTrack?.id) {
						var canvas = this.getRefArrayElem(`trackCanvas_${this.$mouseOverTrack.id}`)?.$el;
						if (canvas) {
							var existsPosition = canvas && $(canvas).position().left;
							var existsEnd = existsPosition + (canvas && $(canvas).width());
							var parentOffset = canvas && $(canvas).parent().offset();
							var canvasClientX = e.clientX - (parentOffset && parentOffset.left || 0);

							var lPos = existsPosition - canvasClientX;
							var rPos = canvasClientX - existsEnd;
							var isSelected = this.selectedTrack?.id === this.$mouseOverTrack.id;

							if (isSelected && lPos < 40 && lPos >= -4) {
								this.activeCanvasMouseOverAction = 'leftTrim';
							} else if (isSelected && rPos < 40 && rPos >= -4) {
								this.activeCanvasMouseOverAction = 'rightTrim';
							} else {
								this.activeCanvasMouseOverAction = 'drag';
							}
						}
					} else {
						this.activeCanvasMouseOverAction = null;
					}
				}

				if (e.buttons !== 1) return;
				var delta = e.movementX;
				var deltaSeconds = (delta/this.display.pixelsPerSecond);
				var deltaSecondsForOffsets = deltaSeconds;
				var mouseDeltaMove = this.activeTrackMouseDownPosition && Math.max(Math.abs(this.activeTrackMouseDownPosition[0] - e.clientX), Math.abs(this.activeTrackMouseDownPosition[1] - e.clientY));

				if (this.selectedTrack && this.activeTrackMouseDownPosition && (mouseDeltaMove > 10 || this.activeTrackChanged)) {
					if (this.activeCanvas) {
						if (this.selectedTrack?.id !== this.activeTrack?.id) return;
						
						let trackDuration = this.activeTrack.__buffer?.duration || this.activeTrack.duration;
						if (this.activeTrack.trimmedDuration === 0) {
							this.activeTrack.startOffset = (trackDuration - 0.1) / 2;
							this.activeTrack.endOffset = (trackDuration - 0.1) / 2;
							this.activeTrack.trimmedDuration = 0.1;
						}
						switch(this.activeCanvasAction) {
							case 'leftTrim':
								if (this.activeTrack.__supportsResizing) {
									this.activeTrack.startOffset = Math.min(trackDuration - 0.1 - this.activeTrack.endOffset, Math.max(0, (this.activeTrack.startOffset || 0) + deltaSecondsForOffsets));
									if (this.activeTrack.startOffset > 0) {
										this.activeTrack.start = Math.max(0, this.activeTrack.start + deltaSeconds);
									}
								} else {
									this.activeTrack.start += deltaSeconds;
									this.activeTrack.updateDuration(Math.max(0.1, trackDuration - deltaSecondsForOffsets));
								}
								break;
							case 'rightTrim':
								if (this.activeTrack.__supportsResizing) {
									this.activeTrack.endOffset = Math.min(trackDuration - 0.1 - this.activeTrack.startOffset, Math.max(0, (this.activeTrack.endOffset || 0) - deltaSecondsForOffsets));
								} else {
									this.activeTrack.updateDuration( Math.max(0.1, trackDuration + deltaSecondsForOffsets));
								}
								break;
							case 'drag':
								var tracks = this.selectedTracks.length ? this.selectedTracks : [this.activeTrack];
								var refreshPlay = false;
								tracks.map(activeTrack => {
									const targetTrack = this.aggregatedTracks.find(f => f.trackNumber === this.$mouseOverAggregatedTrack);
									if (targetTrack?.__readonly) return;
									if (!this.activeTrackNumberChanged) {
										activeTrack.start = Math.max(0, activeTrack.start + deltaSeconds);
									}
									if (!activeTrack.__isSubTrack && activeTrack.trackNumber() !== this.$mouseOverAggregatedTrack && (this.selectedTracks.length <= 1 || this.selectedTracksHasSameAggregatedTrack)) {
										this.activeTrackNumberChanged = this.$mouseOverAggregatedTrack !== this.activeTrackInit[activeTrack.id].__trackNumber;
										activeTrack.start = this.activeTrackInit[activeTrack.id].start;
										activeTrack.insertion.JsonConfig.trackNumber = this.$mouseOverAggregatedTrack;
										refreshPlay = true;
									}
								});
								if (refreshPlay)
									this.refreshPlay();
								break;
						}
						this.activeTrackChanged = true;
					}
				} else {
					if (this.activeTrackMouseDownPosition && this.navigatorPanelMouseDown) {
						const scroll = this.$refs.navigatorScroll && this.$refs.navigatorScroll.instance;
						if (scroll) {
							scroll.scrollTo({
								left: scroll.scrollLeft() - e.movementX,
								top: scroll.scrollTop() - e.movementY
							});
						}
					}
				}

				if (this.activeCanvasAction) {
					e?.stopPropagation();
					e?.preventDefault();
				}
			},
		}
	};
</script>

<style lang='scss'>
	@import "./tracker-styles.scss";

	.audio-tracker {
		@import "main/themes/generated/variables.base.scss";

		.current-time-value {
			position: absolute;
			height: 100%;
			font-size: 10px;
			background: #ff9771;
			padding: 0px 6px;
			color: #ffdfd0;
		}

		.track-infos {
			margin-top: 15px;
			border-bottom: 1px solid rgba(0,0,0,0.1);
		}

		.navigator-scroll {
			border-bottom: 1px solid rgba(0,0,0,0.1);
		}

		.navigator-panel {
			background: #ececec;
		}

		.ruler-holder {
			background: #f2f2f2;
		}

		.tracks-navigator {
			--overflow: auto;
			--width: 100px;
			position: relative;
			margin-top: -1px;

			> .tracks {
				margin-top: 1px;

				.tracker-overlay {
					position: absolute;
					left: 0px;
					top: 0px;
					width: 100%;
					height: 100%;
					pointer-events: none;
					overflow: hidden;
				}
			}

			.current-time {
				position: absolute;
				height: 100%;
				width: 1px;
				background: rgba(255,0,0,0.5);
				pointer-events: none;
				z-index: 100;
			}

			.selection {
				position: absolute;
				height: 100%;
				pointer-events: none;
				background: rgba(242, 135, 79, 0.15);
				border-left: 1px solid rgba(242, 118, 73, 0.5);
				border-right: 1px solid rgba(242, 118, 73, 0.5);
				z-index: 200;
			}

			.selection-point {
				position: absolute;
				height: 100%;
				pointer-events: none;
				border-left: 1px solid rgba(0, 0, 0, 0.1);
				width: 1px;
				z-index: 200;

				&.active {
					border-left: 1px dashed $base-accent;
				}
			}

			&:not(:hover) {
				.selection-point:not(.active) {
					display: none;
				}
			}
		}

		.actions {
			padding: 0px 28px;
			margin-bottom: 6px;
		}

		.tracks-panels {
			padding-bottom: 8px;
		}

		.tracks {
			user-select: none;

			.track {
				width: 100%;
				border-bottom: 1px solid #00000030;
				position: relative;

				.track-borders {
					height: 100%;
					position: absolute;
					top: 0px;
					pointer-events: none;
					z-index: 11;
					width: 100%;

					&.break {
						&.local {
							background: linear-gradient(0deg, rgba(255,250,156,0) 0%, #499aff4d, rgba(255,119,0,0) 100%);
							border-left: 1px solid #afd4ff;
							border-right: 1px solid #afd4ff;
						}
						&.network {
							background: linear-gradient(0deg, rgba(255,250,156,0) 0%, #bb4fff38, rgba(255,119,0,0) 100%);
							border-left: 1px solid #d89aff;
							border-right: 1px solid #d89aff;
						}
					}
				}

				&.loading {
					opacity: 0.5;
				}

				.name-field {
					overflow: hidden;
					text-overflow: ellipsis;
					white-space: nowrap;
					padding: 8px 0px 8px 8px;
					background: #ececec;
					color: #555;
					min-height: 38px;
				}

				&:first-child {
					border-top: 1px solid rgb(213, 213, 213);
				}
				> .track-info-holder {
					width: 200px;
					min-width: 200px;
					height: 100%;
					background: #e0e0e0;

					> .track-info {
						height: 100%;
						width: 100%;
						font-size: 12px;
						border-right: 1px solid #e0e0e0;
					}
				}

				> .track-line {
					background: #ececec;
					position: relative;
					height: 100%;

					.track-line-loader {
						position: absolute;
						width: 100%;
						height: 100%;
						pointer-events: none;
					}

					&.focused {
						background: linear-gradient(180deg, #dedede 0%, rgba(236,236,236,1) 15%, rgba(236,236,236,1) 85%, #dedede 100%);
					}
				}

				&.sub-track {
					.track-line {
						background: #ddd;
					}
				}

				.track-actions {
					padding: 4px;
					background: #ececec;
				}

				.track-canvas {
					position: absolute;
					border-radius: 4px;
					border: 2px solid #d8d8d899;

					> canvas {
						position: absolute;
						height: 100%;
						width: 100%;
					}

					&.mouseover, &.selected {
						&:not(.break) {
							background: #ccc;
						}
						&.break {
							background: linear-gradient(0deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 22%, rgba(255,255,255,1) 79%, rgba(255,255,255,0) 100%);
						}
						border-bottom: 2px solid $base-accent;
					}

					.canvas-actions {
						position: absolute;
						top: 0px;
						left: 0px;
						right: 0px;
						height: 100%;
					}

					&.selected {
						z-index: 10;

						.canvas-actions {
							left: -40px;
							right: -40px;
						}
					}

					&.muted {
						opacity: 0.3;
					}

					&.leftTrim, &.rightTrim {
						cursor: col-resize;
					}

					&.drag {
						cursor: all-scroll;
					}

					&.break {
						border: none;
						border-radius: 0px;
						font-size: 12px;
						color: rgba(0,0,0,0.5);
					}
				}
			}
		}

		.button {
			background: #ffffff40;
			border-radius: 4px;
			height: 30px;
			padding: 0px 8px;
			color: #777;
			user-select: none;
			border: 1px solid #e0e0e0;
			min-width: 30px;

			&.success {
				background: #ff866a;
				color: $base-bg;
				border: 1px solid #ff866a;
			}

			&:hover:not(.disabled) {
				background: $base-accent;
				color: $base-bg;
				cursor: pointer;
			}

			&.is-muted {
				background: #fffc00;
			}

			&.is-solo {
				background: #00ffa6;
			}

			&:not(.no-space) > * {
				&:not(:first-child) {
					margin-left: 4px;
				}
			}
		}

		.hidden {
			display: none;
		}

		.dx-dropdownbutton {
			height: 30px;
		}

		.dx-button {
			font-size: 11px !important;
			height: 30px;
			background: #ffffff40 !important;
			box-shadow: none;
			border: 1px solid #e0e0e0;

			> .dx-button-content {
				padding: 2px 8px;

				svg, i, .dx-button-text {
					color: #777;
				}
			}

			&:hover {
				background: $base-accent !important;
				> .dx-button-content {
					svg, i, .dx-button-text {
						color: $base-bg;
					}
				}
			}
		}

		.no-text .dx-button-content {
			padding-left: 0px !important;
			padding-right: 0px !important;
		}

		.px-loader {
			margin: 4px !important;
			color: #f27849 !important;
		}

		.track-loader {
			background: #ffffff77;
			padding: 2px 4px;
			border-radius: 4px;
		}

		.tools-panel {
			padding: 0px 4px;
		}

		.ruler-scroll {
			border-bottom: 1px solid rgba(0,0,0,0.1);
		}

		.resize-handler {
			position: fixed;
			pointer-events: none;
			top: 0px;
			left: 0px;
			width: 100%;
			height: 100%;
			z-index: -1;
		}

		.bottom-panel {
			border-left: 1px solid rgb(255 255 255 / 10%);
			background: #484856;
			box-shadow: rgba(0, 0, 0, 0.3) 0px 0px 2px 2px;
		}

		.clip-indicator {
			min-width: 50px;
		}
	}
</style>
